import { assign, createMachine, sendParent } from 'xstate';
import { ApplyTypes, AppStatuses } from '../gql/graphql';
import apply from '../networkRequests/apply/apply';
import queryCustomersByIDV from '../networkRequests/customerSearch/queryCustomersByIDV';
import { PropagateNetworkErrors, sharedActions } from './actions/genericActions';
import { identify, page, track } from './actions/segment';
import { AssignApplyResult, AssignCustomerSearchResult } from './actions/sharedNetworkActions';
import { MakeStateCallingQueryOrderWithCartValidation } from './refresh-order';
import { isOrderInUseError, MakeStateCreatingNewOrderForOrderInUse } from './create-new-order';
import { EcommMachineEvents } from './types/ecommMachine.types';
import { OtbEvents, OtbMachineContext, OtbMachineEvents, OtbMachineStates, OtbStates } from './types/otbMachine.types';
import { CustomerFlowType } from '../constants/customer-flow-type';

// Shared helper for OTB apply result selection logic
const makeOtbApplySelectionTransitions = (successTarget: string, retryTarget: string, ineligibleTarget: string = '#OTBCustomer.Ineligible') => [
  {
    cond: 'LTOIneligibleAutoVertical',
    target: '#OTBCustomer.LTOIneligible',
    actions: [assign<OtbMachineContext>({ autoVerticalError: true }), track('Non Leaseable Vertical in State', { errorType: 'Ineligible' })],
  },
  {
    cond: 'LTOIneligible',
    target: '#OTBCustomer.LTOIneligible',
    actions: [track('Non Leaseable State', { errorType: 'Ineligible' })],
  },
  {
    cond: (context: OtbMachineContext) => !context.applyErrors?.length || context.applyErrors[0]?.__typename === 'CartValidation',
    target: successTarget,
  },
  {
    cond: (context: OtbMachineContext) => !!context.applyErrors?.some((err) => err.__typename === 'Ineligible'),
    target: ineligibleTarget,
  },
  {
    target: retryTarget,
  },
];

/** @xstate-layout N4IgpgJg5mDOIC5QHkAqAhAwgV1gFwHsBbMAJwDoBRAOz1IE8BiNdVegBzAG0AGAXUSh2BWAEs8ogtUEgAHogCMAFgCc5AMwA2dQCYAHOqUB2PZr08lAGhD1ESnevIqArEqU89Ko5qWaVmgF8A6xYcfGIyKloGZgw2Ti4FASQQYTEJKRl5BGU1LV0DY1NzKxtEHVUNIwUPJVNtFXV1IJCMMMISCgBBdnZSAgA3AEMAG1jWDm5+GTTxSWkU7OdXch1nFQUFVxUeZz0FTWtbBDWdciMdHgseHQPjIxUWkFDcDsievsHR8fjuJJmRHNMotEP4zs4Hm5Gro1nodEc7MoNLobjxNDwFLCni9wp1yAA5AhdADGxII2FoADFydQIIx8WBZHheMkhICMgtQNkweQISooU0dLD4WUEPtyHVnJpqkopXVNELsW1XhEKAAZIb4anYUiMACS1DJpFIYGJeAAyub8SyAel5llEM59OQFOpoepvDoTCLjuLJdLlHLTIrgs9lbjIhqteTdSxfjaUrMOQ6EE69C63U0PQrvQiTjdVuoLJoakp1FsMQolVgVXio3htbG4pNEqzUuz7SDU87Xe7PbnRUXHJd-ApGp5zBCgqHqAQIHAZDi3qRbUDOXJEABaQ6i7fkK5XHQqFyY-TqPTOavtVVROjHNl24Fcx1GF2qIz2GofoulY5KBTpjwbqyhsvj9letaRAAIqaIyiNQYCrsmXb2Hm6jrLyqgXoeQpokoEERhQlCyKI+DwVAHxwcSQwcpSIwEAA7khnbPjkI4unoRhOh+1QHAoebuK+eweh+TpohsVahkuN4fP0wwjMxT4bggDzpnUZY7MYCrygJSIGJcTRlh+KicZeUnhsuBJEqSNINjSECKeu2QmEo5zGP+Y4YmYdR5uKJlSno9gqF6XoEZZ9aNo5KZus4TjVFofFcQYChGHmFRqJxNReEYNzaP+zTmTWhEEmADGUaI1HIQ+a4ppiPBqABXFesYmwlr5ChOBe3lHiFfj4dOQA */
export default createMachine<OtbMachineContext, OtbMachineEvents, OtbMachineStates>(
  {
    id: 'OTBCustomer',
    initial: OtbStates.InitialState,
    context: {} as OtbMachineContext,
    on: {
      [OtbEvents.TRACK]: {
        actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
      },
      [OtbEvents.PAGE]: {
        actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
      },
      [OtbEvents.IDENTIFY]: {
        actions: (context, event: any) => identify(event?.data?.customerSorId, event?.data?.payload),
      },
    },
    states: {
      [OtbStates.InitialState]: {
        always: [
          { cond: 'Initial', target: OtbStates.LastFour },
          { cond: 'OTBApply', target: OtbStates.PrepareOtbContext },
        ],
      },
      [OtbStates.LastFour]: {
        initial: 'waiting',
        states: {
          waiting: {
            on: {
              [OtbEvents.Last4SSN]: [
                { cond: 'IsLast4Different', actions: [assign({ isLast4Different: true })] }, // TODO: turn this into a state instead of page logic
                // if last 4 matches because the above guard did not trigger and
                // already verified (such as by a previous cust search call) then skip cust search and go straight to apply
                { cond: (context) => context.customerFlowType === CustomerFlowType.VerifiedCustomer, target: 'callingApply' },
                { target: 'callingSearch' },
              ],
              [OtbEvents.Apply]: 'callingApply',
            },
          },
          callingSearch: {
            tags: 'calling',
            invoke: {
              src: queryCustomersByIDV,
              onDone: [
                {
                  cond: 'CustomerOTB',
                  target: 'callingApply',
                  actions: [AssignCustomerSearchResult, track('Passed eligibility'), track('Customer OTB Eligible')],
                },
                // important that this is under the successful path
                {
                  cond: (context) => context.application?.lease?.status === AppStatuses.ErrorsFound,
                  target: '#OTBCustomer.NewApplication',
                  actions: [AssignCustomerSearchResult, track('OTB last4 errors found')], //not exactly sure how this scenario could arise in OTB flow but tracking just in case
                },
                {
                  target: 'waiting',
                  actions: [assign({ isLast4Different: true }), track('OTB last4 not recognized')],
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          callingApply: {
            tags: 'calling',
            entry: (context) => [
              identify(undefined, {
                // dont pass user id that will be handled by cust search
                name: context.basicInfo?.firstName + ' ' + context.basicInfo?.lastName, // for backwards compatibility
                emailAddress: context.basicInfo?.emailAddress,
              }),
            ],
            invoke: {
              src: apply,
              onDone: [
                { cond: (context, event) => !context.hasRetriedWithNewOrder && isOrderInUseError(event.data?.applyErrors), target: 'creatingNewOrder' },
                { target: 'callingQueryOrder', actions: AssignApplyResult },
              ],
              // wait to handle apply result until after query order
              onError: { actions: PropagateNetworkErrors },
            },
          },
          creatingNewOrder: MakeStateCreatingNewOrderForOrderInUse('callingApply'),
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('select'),
          select: {
            always: makeOtbApplySelectionTransitions(
              '#OTBCustomer.PostDecision', // success target
              'waiting', // retry target - let the page handle errors
              '#OTBCustomer.Ineligible', // ineligible target
            ),
          },
        },
        on: {
          // from a button on the customer search page
          [OtbEvents.NotRecognized]: { target: 'NewApplication' }, // main ecomm decides long vs new machine
        },
      },
      [OtbStates.NewApplication]: {
        type: 'final',
        entry: [
          sendParent((context) => ({
            type: EcommMachineEvents.NEW_APPLICATION,
            data: { ...context, customers: [], isLast4Different: false },
          })),
        ],
      },
      // The apply succeeded and the lease is approved: the approval page and everything after it
      // are post-decision-machine's (its RouteApproved shows the otb approval page).
      [OtbStates.PostDecision]: {
        type: 'final',
        entry: sendParent((context) => ({ type: EcommMachineEvents.POST_DECISION, data: { ...context, postDecisionStartingPage: undefined } })),
      },
      [OtbStates.Ineligible]: {},
      [OtbStates.LTOIneligible]: {},
      [OtbStates.PrepareOtbContext]: {
        initial: 'preparing',
        states: {
          preparing: {
            entry: [
              assign((context) => {
                const app = context.application;
                if (!app?.lease?.isOtb || !app?.customer) {
                  return {};
                }

                return {
                  basicInfo: {
                    firstName: app.customer.firstName ?? '',
                    lastName: app.customer.lastName ?? '',
                    emailAddress: app.customer.emailAddress ?? '',
                  },
                  basicInfoContinued: {
                    birthDate: app.customer.birthDate ?? '',
                    taxpayerIdentificationNumber: '',
                    tokenizedTaxId: app.customer.tokenizedTaxId ?? '',
                    monthlyIncome: app.lease?.employment?.monthlyIncome ?? '',
                  },
                  cellPhone: app.customer.cellPhone ?? '',
                  accommodationAddress: app.customer.accommodationAddress
                    ? {
                        line1: app.customer.accommodationAddress.line1 ?? '',
                        line2: app.customer.accommodationAddress.line2 ?? undefined,
                        city: app.customer.accommodationAddress.city ?? '',
                        state: app.customer.accommodationAddress.state ?? '',
                        zip: app.customer.accommodationAddress.zip ?? '',
                      }
                    : undefined,
                  billingAddress: app.customer.billingAddress
                    ? {
                        line1: app.customer.billingAddress.line1 ?? '',
                        line2: app.customer.billingAddress.line2 ?? undefined,
                        city: app.customer.billingAddress.city ?? '',
                        state: app.customer.billingAddress.state ?? '',
                        zip: app.customer.billingAddress.zip ?? '',
                      }
                    : undefined,
                  customerFlowType: CustomerFlowType.SingleCustomer,
                };
              }),
            ],
            always: {
              target: 'callingApply',
            },
          },
          callingApply: {
            tags: 'calling',
            entry: (context) => [
              identify(undefined, {
                // dont pass user id that will be handled by cust search
                name: context.basicInfo?.firstName + ' ' + context.basicInfo?.lastName, // for backwards compatibility
                emailAddress: context.basicInfo?.emailAddress,
              }),
            ],
            invoke: {
              src: apply,
              onDone: [
                { cond: (context, event) => !context.hasRetriedWithNewOrder && isOrderInUseError(event.data?.applyErrors), target: 'creatingNewOrder' },
                { target: 'callingQueryOrder', actions: AssignApplyResult },
              ],
              onError: {
                actions: PropagateNetworkErrors,
                target: '#OTBCustomer.LastFour',
              },
            },
          },
          creatingNewOrder: MakeStateCreatingNewOrderForOrderInUse('callingApply'),
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('select'),
          select: {
            always: makeOtbApplySelectionTransitions(
              '#OTBCustomer.PostDecision', // success target
              '#OTBCustomer.LastFour', // retry target - fall back to regular flow
              '#OTBCustomer.Ineligible', // ineligible target
            ),
          },
        },
      },
    },
    schema: {
      context: {} as OtbMachineContext,
      events: {} as OtbMachineEvents,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      ...sharedActions,
    },
    guards: {
      Initial: (context, event) => !context.initialState,
      OTBApply: (context, event) => context.initialState === 'OTB_APPLY', // TODO: magic string
      CustomerOTB: (context, event: any) => {
        var applyType = event.data?.eligibilityDetails?.applyTypes[0];
        return applyType === ApplyTypes.LeaseOtb || applyType === ApplyTypes.LoanOtb;
      },
      LTOIneligible: (context) => {
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'LeaseRestricted');
        }
        return false;
      },
      LTOIneligibleAutoVertical: (context) => {
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'LeaseRestricted' && err.extensions?.reason === 'AutoVertical');
        }
        return false;
      },
      IsLast4Different: (context, event) => {
        return (
          // for when customer search was already performed but when confirming last4 they did not match
          context.customerFlowType === CustomerFlowType.VerifiedCustomer &&
          !!context.basicInfoContinued?.taxpayerIdentificationNumber &&
          !context.basicInfoContinued.taxpayerIdentificationNumber.endsWith(event.data!.taxIdLast4)
        );
      },
    },
  },
);

// couldn't confirm IDV -> new customer
// confirmed IDV, ineligible -> inelig screen
// confirmed IDV, eligible non OTB -> prefilled new app screen
// confirmed IDV, OTB -> approved screen (then shipping after that, so approved screen has another onNext() case)
